import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { fetchUserInfo, UserInfo } from '../services/userService';
import { setAuthSnapshot, clearAuthSnapshot } from '../utils/authStore';
import { forceReauth } from '../utils/auth';
import { verifySessionIdentity } from '../utils/sessionGuard';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    userInfo: UserInfo | null;
    refreshAuth: () => Promise<void>;
    hasProfile: boolean;
    authProvider: "clever" | "google" | "password" | null;
    /** Backend-validated: user has linked Google OAuth credentials. */
    googleConnected: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public routes that don't need authentication checks
const PUBLIC_ROUTES = [
    '/',
    '/login',
    '/login/forgot-password',
    '/login/mfa',
    '/signup',
    '/checkout',
    '/login/callback',
    '/auth/clever/callback',
    '/auth/set-password',
    '/onboarding/welcome',
    '/subscription/success',
    '/subscription/cancel',
    '/terms',
    '/privacy',
    '/data',
    '/guardian/no-athletes',
];

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [hasProfile, setHasProfile] = useState(false);
    const [authProvider, setAuthProvider] = useState<"clever" | "google" | "password" | null>(
        localStorage.getItem("authProvider") as "clever" | "google" | "password" | null
    );
    const [googleConnected, setGoogleConnected] = useState(false);
    const authCheckCompleted = useRef(false);

    const refreshAuth = React.useCallback(async () => {
        setIsLoading(true);
        authCheckCompleted.current = false;

        try {
            const info = await fetchUserInfo();

            if (info && info.authenticated) {
                setIsAuthenticated(true);
                setUserInfo(info);
                setHasProfile(info.hasProfile);

                // Mirror identity/role in memory so reads do not depend on localStorage.
                const connected = info.googleConnected === true;
                setGoogleConnected(connected);
                setAuthSnapshot({
                    userId: info.id ?? null,
                    role: info.role ?? null,
                    roles: info.roles ?? [],
                    termsAccepted: info.termsAndConditionsAccepted ?? false,
                    googleConnected: connected,
                    internalCoachPreReadEnabled: info.internalCoachPreReadEnabled === true,
                    manageStudentAthletesEnabled: info.manageStudentAthletesEnabled === true,
                });
                const resolvedProvider = (info.authProvider?.toLowerCase() as "clever" | "google" | "password" | null)
                    ?? (connected ? "google" : null)
                    ?? (localStorage.getItem("authProvider") as "clever" | "google" | "password" | null);
                setAuthProvider(resolvedProvider);
                if (resolvedProvider) {
                    localStorage.setItem("authProvider", resolvedProvider);
                } else if (connected) {
                    localStorage.setItem("authProvider", "google");
                }
                authCheckCompleted.current = true;

                // Update localStorage with user info (for backward compatibility with legacy code)
                // NOTE: AuthContext should be the preferred method for accessing user role
                // as it's backend-validated and cannot be manipulated by the user
                if (info.role) {
                    localStorage.setItem('userRole', info.role);
                }
                if (info.hasProfile !== undefined) {
                    localStorage.setItem('hasProfile', String(info.hasProfile));
                }
                if (info.termsAndConditionsAccepted !== undefined) {
                    localStorage.setItem('termsAndConditionsAccepted', String(info.termsAndConditionsAccepted));
                }
                // Persist the NUMERIC user id (info.id), not info.userId — the latter is the
                // login username (email) and would break identity API calls (e.g. /api/athletes/{id}).
                if (info.id != null) {
                    localStorage.setItem('userId', String(info.id));
                    localStorage.setItem('loggedInUserId', String(info.id));
                }
            } else {
                setIsAuthenticated(false);
                setUserInfo(null);
                setHasProfile(false);
                setGoogleConnected(false);
                authCheckCompleted.current = true;
                clearAuthSnapshot();
                // Clear localStorage on authentication failure
                localStorage.removeItem('userId');
                localStorage.removeItem('loggedInUserId');
                localStorage.removeItem('userRole');
                localStorage.removeItem('hasProfile');
            }
        } catch (error) {
            setIsAuthenticated(false);
            setUserInfo(null);
            setHasProfile(false);
            setGoogleConnected(false);
            authCheckCompleted.current = true;
            clearAuthSnapshot();
            // Clear localStorage on error
            localStorage.removeItem('userId');
            localStorage.removeItem('loggedInUserId');
            localStorage.removeItem('userRole');
            localStorage.removeItem('hasProfile');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Skip auth check on public routes to avoid unnecessary API calls
        const currentPath = window.location.pathname;

        // Check if it's an exact match with public routes
        const isPublicRoute = PUBLIC_ROUTES.some(route => currentPath === route);

        // Check if it's the shared athlete profile route (read-only, no auth needed)
        // Pattern: /athlete/:athleteName/:uuid
        const isSharedAthleteProfile = /^\/athlete\/[^/]+\/[a-f0-9-]{36}$/i.test(currentPath);

        const isMarketingHub = currentPath === '/marketinghub' || currentPath.startsWith('/marketinghub/');

        // /guardian/select is authenticated: a full reload must call refreshAuth so
        // RequireAuth has userInfo before the page navigates to /athlete/home.
        if (isPublicRoute || isSharedAthleteProfile || isMarketingHub) {
            setIsLoading(false);
            return;
        }

        // Check if we're coming from Google OAuth callback
        // In this case, localStorage was just populated and should NOT be cleared
        const authProvider = localStorage.getItem('authProvider');
        const isFromGoogleOAuth = authProvider === 'google';

        // IMPORTANT:
        // Do NOT clear localStorage on app boot.
        // Several layout components (e.g., OuterLayout banner) read loggedInUserId/userId on mount.
        // Clearing here creates a race where the banner never loads after a hard refresh.
        // We still clear these values on auth failure inside refreshAuth().
        void isFromGoogleOAuth;

        // Only check auth on protected routes
        refreshAuth();

        return;
    }, [refreshAuth]);

    // Detect a "session swap": all Chrome incognito windows (and normal-profile tabs)
    // share one cookie jar, so signing in as another account elsewhere silently
    // overwrites this tab's session cookie. When the user returns to this tab, re-check
    // the backend identity; if it now belongs to a different user, force a clean
    // re-login with an explanation instead of letting them hit a confusing 403.
    // Only act on a definitive mismatch (a real, different authenticated id) so a
    // transient network failure — indistinguishable from "logged out" here — never
    // signs a still-valid user out.
    useEffect(() => {
        if (!isAuthenticated || userInfo?.id == null) {
            return;
        }
        const expectedId = userInfo.id;
        let checking = false;

        const check = async () => {
            if (checking || document.visibilityState !== 'visible') {
                return;
            }
            checking = true;
            try {
                if ((await verifySessionIdentity(expectedId)) === 'mismatch') {
                    forceReauth('session_changed');
                }
            } finally {
                checking = false;
            }
        };

        window.addEventListener('focus', check);
        document.addEventListener('visibilitychange', check);
        return () => {
            window.removeEventListener('focus', check);
            document.removeEventListener('visibilitychange', check);
        };
    }, [isAuthenticated, userInfo?.id]);

    return (
        <AuthContext.Provider value={{ isAuthenticated, isLoading, userInfo, refreshAuth, hasProfile, authProvider, googleConnected }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
